import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { getBriefingList, addToBriefingList, removeFromBriefingList } from '../../api/briefingList';
import { extractErrorMessage } from '../../api/extractErrorMessage';
import DWLFLoader from '../../components/DWLFLoader';

/*
 * Briefing List tab — manage the symbols that appear in your daily briefing
 * but aren't on your watchlist (Mag7, macro/dollar, sector bellwethers). The
 * briefing covers watchlist ∪ briefing list; an empty briefing list just
 * means the briefing is your watchlist.
 */
const BriefingListTab = () => {
  const [list, setList] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    getBriefingList()
      .then(setList)
      .catch((e) => setError(extractErrorMessage(e, 'Failed to load briefing list')));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const symbol = input.trim();
    if (!symbol) {return;}
    setBusy(true);
    setError('');
    try {
      const res = await addToBriefingList(symbol);
      setList(res?.briefingList || []);
      setInput('');
      setToast(`Added ${symbol} to your briefing`);
    } catch (e) {
      setError(extractErrorMessage(e, `Failed to add ${symbol}`));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (symbol) => {
    setBusy(true);
    setError('');
    try {
      const res = await removeFromBriefingList(symbol);
      setList(res?.briefingList || []);
      setToast(`Removed ${symbol}`);
    } catch (e) {
      setError(extractErrorMessage(e, `Failed to remove ${symbol}`));
    } finally {
      setBusy(false);
    }
  };

  if (list === null) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <DWLFLoader size="medium" />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Briefing list</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Symbols you want in your daily briefing but aren&apos;t actively watching — Mag7, the dollar
        (UUP), sector bellwethers. Your briefing covers your watchlist <strong>plus</strong> this list;
        leave it empty and the briefing is just your watchlist. The whole-universe scan lives in the
        Cycle Screener.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Add symbol"
            placeholder="e.g. AAPL, UUP, BTC/USD"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            sx={{ flex: 1 }}
            disabled={busy}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={busy || !input.trim()}>
            Add
          </Button>
        </Stack>

        {list.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Your briefing list is empty — your briefing currently mirrors your watchlist.
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1}>
            {list.map((symbol) => (
              <Chip
                key={symbol}
                label={symbol}
                onDelete={() => handleRemove(symbol)}
                disabled={busy}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default BriefingListTab;
